import { Card, CardContent } from '@/components/ui/card';
import { BulkInviteForm, RegistrationLinkForm } from '@/components/events/EventInviteForms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';

/**
 * Tenant admin’s events surface — issues bulk invitations or shares a public
 * signup link for company events (AGMs, town halls, training, vendor demos).
 *
 * Uses the same `BulkInviteForm` and `RegistrationLinkForm` as the resident
 * surface; copy is tweaked for the enterprise audience. Server-side, the
 * bulk-invitation and registration-link endpoints accept any role above
 * `resident` in the hierarchy, and `unit_id` is resolved from the user’s
 * `enterprise_tenant_admin` role binding so guests are correctly scoped to
 * the tenant company at the gate.
 */
export function EnterpriseEventsPage() {
  const ctx = useEnterpriseContext();

  if (!ctx) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl">Events</h1>
        <p className="text-sm text-muted-foreground">
          We couldn&apos;t determine your tenant company. Please open this page from your company
          dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl">Company events</h1>
        <p className="text-sm text-muted-foreground">
          Bringing a group of visitors in for an event? Pre-authorise everyone in one go.
        </p>
      </div>

      <Card>
        <CardContent className="pt-5">
          <Tabs defaultValue="link">
            <TabsList className="mb-4">
              <TabsTrigger value="link">Get a signup link</TabsTrigger>
              <TabsTrigger value="bulk" className="text-muted-foreground">
                I already have the attendee list
              </TabsTrigger>
            </TabsList>
            <TabsContent value="link">
              <p className="text-xs text-muted-foreground mb-4">
                Hosting an open event? Share a link — attendees fill in their own details and
                receive their own gate code. Easiest option.
              </p>
              <RegistrationLinkForm
                propertyId={ctx.propertyId}
                titlePlaceholder="2026 AGM — Safari Systems"
              />
            </TabsContent>
            <TabsContent value="bulk">
              <p className="text-xs text-muted-foreground mb-4">
                You know who’s attending. Add their names, phone numbers, and any other details —
                each gets their own invitation with a QR code, valid for the day of the event.
              </p>
              <BulkInviteForm
                propertyId={ctx.propertyId}
                eventNamePlaceholder="Quarterly town hall"
                visitorListLabel="Attendee list"
                visitorRowNoun="Attendee"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
